import { FC } from 'react'
import clsx from 'clsx'
import { IconPaths, TipoReceitaSncr } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button } from 'app/components'
import { ROTULO_TIPO_RECEITA_SNCR, SncrSaldoTipo, isNotificacaoSncr } from '../core/_models'
import { SncrTipoDescricao } from './SncrTipoReceita'
import styles from './styles/SncrSaldoCard.module.css'

type IProps = {
  saldo: SncrSaldoTipo
  /** Integração desligada nesta plataforma: o botão fica desabilitado com explicação. */
  habilitado: boolean
  onRetirar: (tipo: TipoReceitaSncr) => void
}

/**
 * Reserva de um tipo de receita, com a situação e a ação no mesmo lugar: quem vê "abaixo do
 * mínimo" tem o "Retirar" logo abaixo, não no canto da tela. Bloco (RCE/RET) e números avulsos
 * (notificação) só mudam o verbo do botão.
 */
export const SncrSaldoCard: FC<IProps> = ({ saldo, habilitado, onRetirar }) => {
  const { t } = useT()
  const { tipo, alerta, retiradaEmAndamento } = saldo
  const notificacao = isNotificacaoSncr(tipo)
  const podeRetirar = habilitado && !retiradaEmAndamento
  const motivoBloqueio = !habilitado
    ? t('sncr.card.desabilitado')
    : retiradaEmAndamento
    ? t('sncr.card.desabilitado_pendente')
    : undefined

  return (
    <section
      className={clsx(styles.card, alerta && styles.cardAlerta)}
      aria-label={t('sncr.card.aria', { sigla: tipo })}
    >
      <header className={styles.cabecalho}>
        <div>
          <span className={styles.sigla}>{tipo}</span>
          <span className={clsx(styles.nome, 'ms-2')}>{ROTULO_TIPO_RECEITA_SNCR[tipo]}</span>
        </div>
        <span
          className={clsx(
            styles.tarja,
            tipo === TipoReceitaSncr.RET ? styles.etiquetaRetencao : styles.etiquetaNeutra
          )}
        >
          <SncrTipoDescricao tipo={tipo} />
        </span>
      </header>

      <div className={styles.numero}>
        <span className={clsx(styles.valor, alerta ? styles.valorAlerta : styles.valorOk)}>
          {saldo.saldo}
        </span>
        <span className={styles.rotulo}>{t('sncr.card.reservados')}</span>
      </div>

      {/* Retirada em andamento vence o alerta: o bloco já saiu da Anvisa, falta só o registro aqui. */}
      <div
        className={clsx(
          styles.situacao,
          retiradaEmAndamento ? 'bg-light-info' : alerta ? 'bg-light-warning' : 'bg-light-success'
        )}
        role='status'
      >
        <i
          className={clsx(
            'bi',
            retiradaEmAndamento
              ? 'bi-hourglass-split text-info'
              : alerta
              ? 'bi-exclamation-triangle-fill text-warning'
              : 'bi-check-circle-fill text-success'
          )}
          aria-hidden='true'
        />
        <span>
          {retiradaEmAndamento
            ? t('sncr.card.registro_pendente')
            : alerta
            ? t(notificacao ? 'sncr.lista.abaixo_minimo_de_nr' : 'sncr.lista.abaixo_minimo_de', {
                limiar: saldo.limiarAlerta,
              })
            : t('sncr.card.estoque_ok')}
        </span>
      </div>

      <div className={styles.rodape}>
        {/* Abaixo do mínimo a ação é a principal do card; com reserva suficiente ela fica discreta. */}
        <Button
          className={clsx('w-100', alerta && 'btn-primary')}
          variant={alerta ? undefined : 'secondary-outline'}
          icon={IconPaths.Add}
          onClick={() => onRetirar(tipo)}
          disabled={!podeRetirar}
          title={motivoBloqueio}
          ariaLabel={t(notificacao ? 'sncr.alerta.retirar_aria_nr' : 'sncr.alerta.retirar_aria', {
            sigla: tipo,
          })}
        >
          {t(notificacao ? 'sncr.alerta.retirar_nr' : 'sncr.alerta.retirar')}
        </Button>
      </div>
    </section>
  )
}
